import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import io from 'socket.io-client';
import axios from 'axios';
import './ChatPanel.css';

class SystemChatErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('??? [SISTEMA] Error en SystemChatPanel:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="chat-panel">
          <div className="chat-header">
            <h2>???? Mensajes Autom??ticos del Sistema</h2>
            <p className="header-subtitle">Ocurri?? un error al cargar esta vista</p>
          </div>
          <div className="chat-content">
            <div className="no-contact-selected">
              <p>?????? Error inesperado. Revisa la consola del navegador.</p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const SystemChatPanel = () => {
  const { logout } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchById, setSearchById] = useState('');
  const socketRef = useRef(null);
  const selectedContactRef = useRef(selectedContact);

  useEffect(() => {
    selectedContactRef.current = selectedContact;
  }, [selectedContact]);

  const normalizePhone = (phone) => {
    if (!phone) return '';
    return phone.replace(/\D/g, '').replace(/^1/, '');
  };

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/contacts');
      console.log('???? [SISTEMA] Contactos cargados:', response.data.length);
      setContacts(response.data);
    } catch (error) {
      console.error('??? [SISTEMA] Error cargando contactos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSystemMessages = async (phoneNumber) => {
    try {
      console.log('[SISTEMA] Cargando mensajes autom??ticos para:', phoneNumber);
      const response = await axios.get(`/api/system-messages/${phoneNumber}`);

      if (response.data.isSystemChat) {
        console.log('??? [SISTEMA] Mensajes autom??ticos cargados:', response.data.messages.length);
        setMessages(response.data.messages || []);
      }
    } catch (error) {
      console.error('??? [SISTEMA] Error cargando mensajes:', error);
      setMessages([]);
    }
  };

  const handleSelectContact = (contact) => {
    console.log('[SISTEMA] Contacto seleccionado:', contact.name);
    setSelectedContact(contact);
    loadSystemMessages(contact.phoneNumber);
  };

  useEffect(() => {
    socketRef.current = io(process.env.REACT_APP_API_URL || 'http://localhost:3000', {
      transports: ['websocket', 'polling']
    });

    socketRef.current.on('connect', () => {
      console.log('??? [SISTEMA] Conectado a Socket.io');
    });

    socketRef.current.on('disconnect', () => {
      console.log('??? [SISTEMA] Desconectado de Socket.io');
    });

    socketRef.current.on('new-message', (message) => {
      if (message.isSystemMessage) {
        const currentContact = selectedContactRef.current;
        if (!currentContact) return;

        const contactPhone = normalizePhone(currentContact.phoneNumber);
        const messagePhone = normalizePhone(message.phoneNumber);

        if (contactPhone === messagePhone) {
          setMessages((prev) => {
            const exists = prev.some((m) => m._id === message._id || m.messageId === message.messageId);
            if (exists) return prev;

            const updated = [...prev, message];
            return updated.sort((a, b) =>
              new Date(a.timestamp || a.createdAt) - new Date(b.timestamp || b.createdAt)
            );
          });
        }
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const filteredContacts = contacts.filter((contact) => {
    if (searchById) {
      return contact.mikrowispId && contact.mikrowispId.toString().includes(searchById);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        contact.name?.toLowerCase().includes(term) ||
        contact.phoneNumber?.includes(term) ||
        contact.mikrowispId?.toString().includes(term)
      );
    }
    return true;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diff = now - date;
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));

      if (days === 0) {
        return date.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' });
      }
      if (days === 1) {
        return 'Ayer';
      }
      if (days < 7) {
        return date.toLocaleDateString('es-DO', { weekday: 'short' });
      }
      return date.toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit' });
    } catch {
      return '';
    }
  };

  const renderMessage = (msg) => {
    const isOutbound = msg.direction === 'outbound';

    return (
      <div key={msg._id || msg.messageId} className={`message ${isOutbound ? 'outbound' : 'inbound'} system-message`}>
        <div className="message-bubble">
          {msg.type === 'text' && <p className="message-text">{msg.content?.text}</p>}
          {msg.type === 'template' && (
            <div className="template-message">
              <p className="template-name">???? {msg.content?.templateName}</p>
              {msg.content?.text && <p className="message-text">{msg.content.text}</p>}
            </div>
          )}
          <div className="message-footer">
            <span className="message-time">{formatDate(msg.timestamp || msg.createdAt)}</span>
            {msg.systemHeader && <span className="system-badge">???? {msg.systemHeader}</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="chat-panel">
      <div className="chat-header">
        <h2>???? Mensajes Autom??ticos del Sistema</h2>
        <p className="header-subtitle">Solo lectura - Ver mensajes enviados por el sistema</p>
        <button onClick={logout} className="logout-button">Cerrar Sesi??n</button>
      </div>

      <div className="chat-content">
        <div className="contacts-panel">
          <div className="contacts-header">
            <h3>Contactos</h3>
            <input
              type="text"
              placeholder="Buscar por nombre o tel??fono..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <input
              type="text"
              placeholder="Buscar por ID de cliente..."
              value={searchById}
              onChange={(e) => setSearchById(e.target.value)}
              className="search-input"
            />
          </div>

          {loading ? (
            <div className="loading">Cargando contactos...</div>
          ) : (
            <div className="contacts-list">
              {filteredContacts.map((contact) => (
                <div
                  key={contact._id}
                  className={`contact-item ${selectedContact?._id === contact._id ? 'active' : ''}`}
                  onClick={() => handleSelectContact(contact)}
                >
                  <div className="contact-info">
                    <div className="contact-name">{contact.name || contact.phoneNumber}</div>
                    <div className="contact-phone">{contact.phoneNumber}</div>
                    {contact.mikrowispId && <div className="contact-id">ID: {contact.mikrowispId}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="chat-window">
          {selectedContact ? (
            <>
              <div className="chat-window-header">
                <h3>{selectedContact.name || selectedContact.phoneNumber}</h3>
                <p className="readonly-badge">???? Solo lectura - Mensajes autom??ticos</p>
              </div>

              <div className="messages-container">
                {messages.length === 0 ? (
                  <div className="no-messages">
                    <p>???? No hay mensajes autom??ticos para este contacto</p>
                  </div>
                ) : (
                  messages.map((msg) => renderMessage(msg))
                )}
              </div>
            </>
          ) : (
            <div className="no-contact-selected">
              <p>???? Selecciona un contacto para ver sus mensajes autom??ticos</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SystemChatPanelWithBoundary = () => (
  <SystemChatErrorBoundary>
    <SystemChatPanel />
  </SystemChatErrorBoundary>
);

export default SystemChatPanelWithBoundary;
